import React, { FC, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
	EvBodyDefault,
	EvFlexColumn,
	EvFlexRow,
	EvHeadingLarge,
	EvIcon,
	EvLink,
	EvSection,
	EvTabs,
	SmallBackIcon
} from '@evinced-private/ui-common';

import {
	ALL_ISSUES_TABLE_ID,
	UNIQUE_ISSUES_TABLE_ID
} from 'src/apps/dashboard/constants/Dashboard';
import { handleDashboardRedirection } from 'src/apps/dashboard/helpers/NavigationHelpers';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { isConfigurationReady, isDashboardEnabled } from 'src/common/helpers/ConfigurationHelpers';
import { UNIQUE_QUERY_PARAM } from 'src/common/helpers/QueryParamsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';

import { IssuesFilters } from './components/IssuesFilters';
import { ALL_ISSUES_MOCK_DATA, UNIQUE_ISSUES_MOCK_DATA } from './helpers/IssuesMockData';
import { getAllIssuesColumns, getUniqueIssuesColumns } from './helpers/IssuesTableColumns';
import { useFiltersWithQueryParams } from './hooks/useFiltersWithQueryParams';
import { useIssuesPageFilters } from './hooks/useIssuesPageFilters';
import { ISSUES_PAGE_CONSTANTS } from './IssuesPage.constants';

import './IssuesPage.scss';

const IssuesPage: FC = () => {
	const location = useLocation();
	const history = useHistory();

	usePlatformConfigInitializer();

	const { filterDefinitions, filterOptions } = useIssuesPageFilters();

	const { configuration } = useConfiguration();
	const { filters, handleFilterChange } = useFiltersWithQueryParams({
		filterDefinitions,
		filterOptions
	});

	const isUnique = useMemo(() => {
		return new URLSearchParams(location.search).get(UNIQUE_QUERY_PARAM) === 'true';
	}, [location.search]);

	const activeTabIndex = isUnique ? 1 : 0;

	const backLinkContent = (
		<EvFlexRow gap="SMALL" align="CENTER">
			<EvIcon icon={SmallBackIcon} small />
			<span>{ISSUES_PAGE_CONSTANTS.BACK_BUTTON_TEXT}</span>
		</EvFlexRow>
	);

	const handleBackClick = (): void => {
		history.push(RoutesHelper.getAccessibilityDashboardPath());
	};

	const handleTabSelected = useCallback(
		(index: number) => {
			const searchParams = new URLSearchParams(location.search);
			if (index === 1) {
				searchParams.set(UNIQUE_QUERY_PARAM, 'true');
			} else {
				searchParams.delete(UNIQUE_QUERY_PARAM);
			}
			const newSearch = searchParams.toString();
			history.replace({
				pathname: location?.pathname,
				search: `?${newSearch ?? ''}`
			});
		},
		[history, location.pathname, location.search]
	);

	const tabs = useMemo(
		() => [
			{
				id: 'all-issues-tab',
				tablistId: 'issues-page-tabs',
				ref: null,
				isActive: activeTabIndex === 0,
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				handleKeyDown: () => {},
				children: 'All issues',
				ariaControls: 'all-issues-panel',
				dataAutomationId: 'all-issues-tab'
			},
			{
				id: 'unique-issues-tab',
				tablistId: 'issues-page-tabs',
				ref: null,
				isActive: activeTabIndex === 1,
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				handleKeyDown: () => {},
				children: 'Unique issues',
				ariaControls: 'unique-issues-panel',
				dataAutomationId: 'unique-issues-tab'
			}
		],
		[activeTabIndex]
	);

	const tabPanels = useMemo(
		() => [
			{
				id: 'all-issues-panel',
				ariaLabelledBy: 'all-issues-tab',
				isActive: activeTabIndex === 0,
				children: (
					<EvSection ariaLabel="All Issues Table" className="issues-table-section">
						<div className="issues-table-container">
							<PlatformTable
								tableId={ALL_ISSUES_TABLE_ID}
								columns={getAllIssuesColumns()}
								tableData={ALL_ISSUES_MOCK_DATA}
								totalCount={ALL_ISSUES_MOCK_DATA.length}
								options={{
									keyField: 'id',
									pagination: true,
									dataType: 'issues',
									title: 'All Issues',
									caption: 'All Issues Table'
								}}
							/>
						</div>
					</EvSection>
				)
			},
			{
				id: 'unique-issues-panel',
				ariaLabelledBy: 'unique-issues-tab',
				isActive: activeTabIndex === 1,
				children: (
					<EvSection ariaLabel="Unique Issues Table" className="issues-table-section">
						<div className="issues-table-container">
							<PlatformTable
								tableId={UNIQUE_ISSUES_TABLE_ID}
								columns={getUniqueIssuesColumns()}
								tableData={UNIQUE_ISSUES_MOCK_DATA}
								totalCount={UNIQUE_ISSUES_MOCK_DATA.length}
								options={{
									keyField: 'id',
									pagination: true,
									dataType: 'issues',
									title: 'Unique Issues',
									caption: 'Unique Issues Table'
								}}
							/>
						</div>
					</EvSection>
				)
			}
		],
		[activeTabIndex]
	);

	if (!isConfigurationReady(configuration)) {
		return null;
	}

	if (!isDashboardEnabled(configuration)) {
		handleDashboardRedirection(history, configuration);
		return null;
	}

	return (
		<div className="issues-page">
			<EvFlexColumn gap="MEDIUM">
				<div className="issues-page__back-link">
					<EvLink
						url={RoutesHelper.getAccessibilityDashboardPath()}
						customLink={backLinkContent}
						onClick={handleBackClick}
					/>
				</div>

				<EvFlexColumn gap="SMALL">
					<EvHeadingLarge id="issues-page-title">{ISSUES_PAGE_CONSTANTS.TITLE}</EvHeadingLarge>
					<EvBodyDefault id="issues-page-description">
						{ISSUES_PAGE_CONSTANTS.DESCRIPTION}
					</EvBodyDefault>
				</EvFlexColumn>

				<IssuesFilters
					filters={filters}
					onFilterChange={handleFilterChange}
					filterDefinitions={filterDefinitions}
					filterOptions={filterOptions}
				/>

				<section className="issues-tabs-section">
					<EvTabs
						key={`issues-tabs-${activeTabIndex}`}
						id="issues-page-tabs"
						tabs={tabs}
						tabPanels={tabPanels}
						defaultTab={activeTabIndex}
						onTabSelected={handleTabSelected}
						ariaLabel="Issues tabs"
					/>
				</section>
			</EvFlexColumn>
		</div>
	);
};
export default IssuesPage;
